import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type CSSProperties, type ReactElement, type ReactNode, type SVGProps } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, FileCheck, Gamepad2, ListChecks, Loader2, LogIn, Swords, Timer, X, Zap } from 'lucide-react';

import { env } from '@/env';
import { useActiveMatchContext } from '@/contexts/ActiveMatchContext';
import { useMainCta } from '@/contexts/mainCta';
import { useSfideBarra } from '@/contexts/SfideBarraContext';
import { useModoBarraAzione } from '@/lib/modoBarraAzione';
import { useCountdown } from '@/hooks/useCountdown';
import { useHaptics } from '@/hooks/useHaptics';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { useIngombroBasso } from '@/hooks/useIngombroBasso';
import { useLimuloPolling, useLimuloPollingGuida } from '@/hooks/useLimuloPolling';
import {
  azioniDalMotore,
  azioniDelTorneo,
  destinazioneDi,
  urgentiDelBottone,
  divisioneSonde,
  eSchedaIncontro,
  eTavoloVivoPerRotta,
  finestraCheckInAperta,
  finestraCheckInApertaDalMotore,
  ordinaAzioni,
  riapreLaBarra,
  codaHaUnaScheda,
  profileTypeDelBiglietto,
  rigaCodaOf,
  sezioneDellaCodaOf,
  suPannelloDellaCodaOf,
  torneiDaRiconoscere,
  type LimuloRigaCoda,
  type LimuloAzionePendente,
  type LimuloTipoAzione,
  type LimuloVoceAzione,
} from '@/lib/limulo/azionePersistente';
import { matchStateOf } from '@/lib/limulo/match';
import { isTournamentTerminal, LIMULO_POLL_LISTA_MS, LIMULO_POLL_TORNEO_MS } from '@/lib/limulo/polling';
import { chessLimuloAcceso } from '@/lib/chess/chessLimuloAcceso';
import { servizioTavoliDellaBuild } from '@/lib/limulo/servizioTavoli';
import { tennisClashDellaBuild } from '@/lib/limulo/tennisClashBuild';
import { LIMULO_MINE_URL } from '@/lib/limulo/routes';
import { cn } from '@/lib/cn';
import {
  useGetLimuloAzioniQuery,
  useGetLimuloGamesQuery,
  useGetLimuloMatchesQuery,
  useGetLimuloMatchQuery,
  useGetLimuloQueuesQuery,
  useGetLimuloRegistrationQuery,
  useGetLimuloTournamentQuery,
  useGetMyLimuloTournamentsConTotaleQuery,
  LIMULO_MINE_LIMIT_MAX,
} from '@/services/limuloApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useCodeTornei } from '@/contexts/CodeTorneiContext';
import { ModaleCheckIn } from './ModaleCheckIn';
import { CODE_GAME_NAMES, formatElapsed, ticketsToTell } from '@/lib/limulo/code';
import { giochiConCodeDaRiprendere, sitngoDiQtournamentsAcceso } from '@/lib/limulo/sitngoAcceso';
import { BACKGAMMON_GAME_ID } from '@/lib/gameFeatures';
import { BACKGAMMON_PROFILE_TYPE } from '@/lib/qgammon/lobbyTornei';
import { useTitoloAzione } from '@/hooks/useTitoloAzione';
import { apriSchedaDellaCoda } from '@/lib/limulo/tornaAllaCoda';
import type { LimuloMatch, LimuloTournament } from '@/types/limulo';

/**
 * 🔴 L'AZIONE PERSISTENTE — la barra sopra la barra in basso (A) che, minimizzata, diventa il bottone
 * flottante (B). Luca, 2026-09-25T13:14:47Z, L-2195, testuale: «A vedendo tutte le opzioni -
 * se/quando si minimizza quella tab e rimangono azioni da fare diventa B.»
 *
 * Cosa mostra e da dove lo prende sta in `lib/limulo/azionePersistente.ts` (per gli incontri: la
 * faccia del bottone unico della scheda, la stessa regola). Qui ci sono le SONDE che leggono i dati
 * e il disegno.
 *
 * **Derivazioni del cantiere, non parole di Luca** (tma-orc 13:16:12Z: «tienile come derivazioni
 * dichiarate»):
 *  (a) si minimizza con la freccia sulla barra; il tocco sul bottone flottante RIAPRE la barra —
 *      il foglio-elenco di B non l'ha scelto nessuno;
 *  (b) minimizzata resta B finche' compare un'azione NUOVA fra le due che si perdono a tavolino
 *      (invito, check-in d'incontro): allora la barra si riapre da sola. La memoria dura la sessione.
 *
 * ⚠️ Cosa NON fa: non si chiude a mano (sparisce quando il server spegne l'azione); non compare
 * sulla schermata a cui porta; non compare mentre c'e' una partita real-time viva (la pastiglia di
 * `ActiveMatchPill` ha la precedenza: una partita in corso batte un promemoria); se una lettura
 * fallisce non dice niente — l'errore di un'azione lo dice la scheda (ADR-0115).
 */

/** Gli UNICI tipi che `useSfideBarra()` puo' legittimamente portare — v. il filtro qui sotto. */
const TIPI_DAL_CONTESTO: ReadonlySet<LimuloTipoAzione> = new Set(['sfida']);


// PONTE PROVVISORIO I-1317 (A7: la destinazione della riga del ponte, SOLO nel letterale) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
// Solo una rotta INTERNA (comincia con «/», non con «//», senza schema): la costruisce la fonte del ponte dal `conversationId`, mai dal testo.
// Ogni altro valore ⇒ `null`, e vale `destinazioneDi` di sempre.
// eslint-disable-next-line react-refresh/only-export-components -- esportata solo per il banco del ponte (tests/ponte-i1317-barra.test.tsx)
export function destinazionePonteI1317(a: LimuloAzionePendente): string | null {
  if (!(import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true')) return null;
  return typeof a.destinazionePonteI1317 === 'string' && /^\/(?!\/)[^:\\]*$/.test(a.destinazionePonteI1317) ? a.destinazionePonteI1317 : null;
}

export function AzionePersistente({ insetBottom = false, Riga = RigaAzione }: { insetBottom?: boolean; Riga?: RigaAzioneComponent }) {
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  // Stesso cancello della sezione Limulo: il flag di build E il ruolo beta (v. `routes/router.tsx`).
  const isBeta = useHasLogtoRole('beta');
  // 🔴 [I-252/I-254, T2-5, disegno (b) di tma-orcr 28/9 18:07Z] La sfida di piattaforma arriva da FUORI
  // questo file: `useSfideBarra()` e' un `useContext` con default `[]`, che non tocca Redux ne' RTK
  // Query — senza `<SfideBarraProvider>` a monte (i banchi che montano `<AzionePersistente>` da soli)
  // resta sempre `[]`, per costruzione, IDENTICO a prima. Chi la produce vive in
  // `contexts/SfideBarraContext.tsx`, montato in `AppShell.tsx` attorno alla sola riga di questo
  // componente.
  // ⛔ **Filtro difensivo, come per le voci del motore** (`TIPO_DEL_KIND`, piu' sotto — una struttura
  // di lookup, mai un oggetto letterale): un contesto che un giorno portasse un `tipo` diverso da
  // `sfida` viene scartato qui, mai fidato alla cieca.
  const sfideDalContesto = useSfideBarra();
  const sfide = useMemo(() => sfideDalContesto.filter((a) => TIPI_DAL_CONTESTO.has(a.tipo)), [sfideDalContesto]);

  if (!env.featureLimuloEnabled || !isBeta || !authed) return null;
  // Le azioni da UNA lettura del motore (kb#275), a flag acceso; a flag spento le sonde di prima.
  return env.featureTournamentActionsEnabled ? (
    <AzioniDalMotore insetBottom={insetBottom} sfide={sfide} Riga={Riga} />
  ) : (
    <RaccoltaAzioni insetBottom={insetBottom} sfide={sfide} Riga={Riga} />
  );
}

const NESSUNA_VOCE: readonly LimuloVoceAzione[] = [];

/**
 * 🔑 **UNA LETTURA al posto delle sonde** (`VITE_FEATURE_TOURNAMENT_ACTIONS_ENABLED`, kb#275): le voci,
 * il loro ordine e le loro scadenze li decide il motore (`azioniDalMotore`). Stessa barra, stesse
 * regole del client: `tavoliFiniti`, la riga «In coda», nessun orologio.
 *  · ritmo: 12 s con una finestra di check-in d'incontro aperta per il server, altrimenti 30 s — la
 *    stessa regola delle sonde, col backoff di `useLimuloPolling` sui fallimenti;
 *  · `truncated` (oltre i tetti del motore) ⇒ la nota «almeno 1 torneo non controllato»;
 *  · su errore la barra tace, come prima (ADR-0115): l'errore di un'azione lo dice la scheda.
 * ⚠️ Il gioco del torneo la voce non lo porta: serve solo all'invito (tag CR o tavolo QGammon) e si
 * legge dalla scheda torneo, una volta e senza polling (`SondaGioco`).
 * ⚠️ Differenze note, solo staging (A7 e i due casi limite di A3, revisione di #422):
 *  · il «gioca» di un tavolo QGammon non guarda piu' se il tavolo sta sul QGammon di QUESTA build
 *    (`servizioTavoliDellaBuild`): la voce del motore non porta l'endpoint, e la scheda a cui porta lo
 *    decide col suo bottone unico;
 *  · QGammon con `wsUrl` vuoto: il motore non scrive il tavolo (`tavoloDa` lo vuole pieno), ma
 *    `inviteRef` c'e' (`providerData.matchId`) e la voce `game-invite` pure ⇒ qui e' un «gioca», dove la
 *    scheda non offre nessun tavolo;
 *  · Clash Royale senza tag (`remoteMatchTag` assente): `inviteRef` e' `providerData.matchId` o `null`, e
 *    senza tag l'invito e' un «gioca» — come la barra delle sonde, dove `crInviteOf` senza tag non dava
 *    un invito. ⚠️ Il client non distingue un tag da un id: se un giorno un `providerData` CR portasse
 *    `matchId` senza tag, qui diventerebbe «invito · <matchId>». Oggi non e' raggiungibile: il provider CR
 *    non scrive `matchId` (`app-game-clash-royale/backend/provider.js:1093` @ `qlash-limulo@7e0eae2`,
 *    revisione di #424);
 *  · un incontro CR pendente SENZA `externalMatch`: la barra delle sonde diceva «gioca»
 *    (`bottoneUnico.ts:239` non guarda `externalMatch`), il motore non da' nessun `game-invite`
 *    (`azioni.js:136` lo richiede) ⇒ qui niente. Meno rumore, ed e' una differenza (revisione di #424);
 *  · residuo gemello di quello di `SondaGioco`: se fallisce il CATALOGO giochi (`useGetLimuloGamesQuery`),
 *    `profiloDi` e' `null` per tutti e ogni invito CR resta un «gioca». Per il catalogo non c'e' riprova:
 *    si recupera a un focus, a un'invalidazione o a un rimontaggio.
 */
function AzioniDalMotore({ insetBottom, sfide, Riga }: { insetBottom: boolean; sfide: readonly LimuloAzionePendente[]; Riga: RigaAzioneComponent }) {
  const polling = useLimuloPolling();
  const lettura = useGetLimuloAzioniQuery(undefined, polling.ritmo);
  const voci = lettura.data?.items ?? NESSUNA_VOCE;
  useLimuloPollingGuida(
    polling,
    lettura,
    finestraCheckInApertaDalMotore(voci) ? LIMULO_POLL_TORNEO_MS : LIMULO_POLL_LISTA_MS,
  );
  const { data: giochi } = useGetLimuloGamesQuery(undefined);
  const finite = useAppSelector((s) => s.activeMatch.finite);
  const tavoliFiniti = useMemo(() => new Set(finite), [finite]);

  const [giocoDi, setGiocoDi] = useState<Record<string, string | null>>({});
  const suGioco = useCallback((id: string, gameId: string | null) => {
    setGiocoDi((prev) => (prev[id] === gameId ? prev : { ...prev, [id]: gameId }));
  }, []);
  const daRiconoscere = useMemo(() => torneiDaRiconoscere(voci), [voci]);

  const azioni = useMemo(
    () =>
      azioniDalMotore({
        voci,
        profiloDi: (id) => {
          const gameId = giocoDi[id] ?? null;
          return gameId === null ? null : (giochi?.find((g) => g.gameId === gameId)?.profileType ?? null);
        },
        tavoliFiniti,
      }),
    [voci, giocoDi, giochi, tavoliFiniti],
  );
  const troncata = lettura.data?.truncated === true;
  // 🔴 [correzione 28/9, poi bloccante 1 della revisione 19:23Z] La sfida di piattaforma NON viene dal
  // motore: si aggiunge QUI, all'unico punto d'uscita di questo ramo, IN CODA e SENZA `ordinaAzioni`,
  // che qui non si applica (il commento sopra `AzioniDalMotore` lo dice: «la barra mostra quell'ordine
  // [del server]»). Un `ordinaAzioni` sul tutto RIORDINEREBBE anche le voci del motore secondo
  // `LIMULO_TIPI_AZIONE` (misurato: `tests/azione-persistente-dal-motore-client-vero.test.tsx`,
  // invito prima di checkin-incontro per priorita' del motore). In CODA, mai in testa: davanti al
  // server spostava il suo check-in d'incontro sotto «+N», e nel modo «riga» lo rendeva
  // irraggiungibile. Con una sfida presente la prima riga e' quella che c'era senza.
  const conSfide = useMemo(() => (sfide.length > 0 ? [...azioni, ...sfide] : azioni), [azioni, sfide]);

  return (
    <>
      {daRiconoscere.map((id) => (
        <SondaGioco key={id} id={id} suGioco={suGioco} />
      ))}
      <BarraAzioni azioni={conSfide} nonControllati={troncata ? 1 : 0} almeno={troncata} insetBottom={insetBottom} Riga={Riga} />
      <ModaleCheckIn azioni={conSfide} />
    </>
  );
}

/**
 * Quante volte si rilegge la scheda torneo che non e' arrivata, e il primo intervallo (poi raddoppia:
 * 30, 60, 120 s). ⚠️ Una SCELTA, non una misura (revisione di #422, punto c).
 */
const RIPROVE_GIOCO = 3;
const RIPROVA_GIOCO_MS = 30_000;

/**
 * Il gioco di un torneo con un invito. Senza polling: il gioco di un torneo non cambia.
 * 🔴 **Se la lettura fallisce** il gioco resta ignoto, e l'invito CR e' un «gioca» senza tag, che NON
 * riapre la barra minimizzata (`LIMULO_AZIONI_CHE_RIAPRONO`): proprio l'azione che si perde a tavolino.
 * ⇒ Si rilegge, al piu' `RIPROVE_GIOCO` volte. Quando il gioco arriva, l'invito diventa «invito · #TAG»
 * con una chiave NUOVA, e la barra minimizzata si riapre da sola (`riapreLaBarra`).
 * ⚠️ «1+3» sono letture RTK: in HTTP sono fino al doppio, perche' su rete o 5xx il baseQuery ritenta una
 * volta (`limuloBaseQuery`, revisione di #424: misurato con 503, 8 GET).
 * ⚠️ Residuo dichiarato: dopo l'ultima riprova, fino al prossimo montaggio (un torneo che esce e rientra,
 * un'app riaperta), l'invito resta un «gioca». Il tocco porta comunque alla scheda d'incontro, dove il
 * bottone unico mostra l'invito vero.
 */
function SondaGioco({ id, suGioco }: { id: string; suGioco: (id: string, gameId: string | null) => void }) {
  const { data, isError, refetch } = useGetLimuloTournamentQuery(id);
  const gameId = data?.gameId || null;
  const [riprove, setRiprove] = useState(0);
  useEffect(() => {
    suGioco(id, gameId);
  }, [id, gameId, suGioco]);
  useEffect(() => {
    if (!isError || gameId !== null || riprove >= RIPROVE_GIOCO) return;
    const h = window.setTimeout(() => {
      setRiprove((n) => n + 1);
      void refetch();
    }, RIPROVA_GIOCO_MS * 2 ** riprove);
    return () => window.clearTimeout(h);
  }, [isError, gameId, riprove, refetch]);
  return null;
}

/**
 * Quanti tornei si sondano al massimo, PER GRUPPO (in corso · non iniziati: `divisioneSonde`). Tiene
 * il costo indipendente dallo storico. ⚠️ E' una SCELTA, non una misura — e oltre il tetto la barra
 * DICE quanti tornei non ha controllato (revisione #385, punto 1): niente silenzio.
 */
const MAX_TORNEI = 5;

const idDi = (t: LimuloTournament): string => t.tournamentId || t.id;
const firmaDi = (azioni: readonly LimuloAzionePendente[]): string =>
  azioni.map((a) => `${a.chiave}|${a.scadenza ?? ''}|${a.tag ?? ''}|${a.round ?? ''}`).join(';');

function RaccoltaAzioni({ insetBottom, sfide, Riga }: { insetBottom: boolean; sfide: readonly LimuloAzionePendente[]; Riga: RigaAzioneComponent }) {
  const polling = useLimuloPolling();
  // Col `limit` massimo e il totale (revisione #385, 1b): oltre la prima pagina non si tace.
  const mine = useGetMyLimuloTournamentsConTotaleQuery(undefined, polling.ritmo);
  useLimuloPollingGuida(polling, mine, LIMULO_POLL_LISTA_MS);
  const { data: giochi } = useGetLimuloGamesQuery(undefined);

  // ⚠️ «Non so» non e' «finito»: un torneo senza stato riconosciuto resta vivo (`isTournamentTerminal`).
  const { completi, soloIscrizione, nonControllati: oltreIlTetto } = useMemo(
    () => divisioneSonde((mine.data?.items ?? []).filter((t) => !isTournamentTerminal(t)), MAX_TORNEI),
    [mine.data],
  );
  // Oltre la pagina: col totale si CONTANO (di qualunque stato: non li abbiamo guardati); senza
  // totale e con la pagina piena se ne sa solo «almeno uno».
  const righe = mine.data?.items.length ?? 0;
  const totale = mine.data?.total ?? null;
  const oltreLaPagina = totale !== null ? Math.max(0, totale - righe) : righe >= LIMULO_MINE_LIMIT_MAX ? 1 : 0;
  const nonControllati = oltreIlTetto + oltreLaPagina;
  const almeno = totale === null && oltreLaPagina > 0;
  const sondati = useMemo(() => [...completi, ...soloIscrizione], [completi, soloIscrizione]);

  const [perTorneo, setPerTorneo] = useState<Record<string, readonly LimuloAzionePendente[]>>({});
  const riporta = useCallback((id: string, azioni: readonly LimuloAzionePendente[]) => {
    setPerTorneo((prev) => (firmaDi(prev[id] ?? []) === firmaDi(azioni) ? prev : { ...prev, [id]: azioni }));
  }, []);

  const azioni = useMemo(
    () => ordinaAzioni([...sondati.flatMap((t) => perTorneo[idDi(t)] ?? []), ...sfide]),
    [sondati, perTorneo, sfide],
  );

  return (
    <>
      {sondati.map((t) => (
        <SondaTorneo
          key={idDi(t)}
          torneo={t}
          soloIscrizione={!completi.includes(t)}
          profileType={giochi?.find((g) => g.gameId === t.gameId)?.profileType ?? null}
          riporta={riporta}
        />
      ))}
      <BarraAzioni azioni={azioni} nonControllati={nonControllati} almeno={almeno} insetBottom={insetBottom} Riga={Riga} />
    </>
  );
}

/**
 * Serve il dettaglio? Solo l'incontro pendente con una partita esterna: e' li' che puo' esserci
 * l'invito, e il tag (`externalData`) la lista non lo porta — il bordo lo esclude apposta
 * (`incontri.js:767`, `{ externalData: 0 }`).
 */
const serveIlDettaglio = (m: LimuloMatch): boolean => matchStateOf(m) === 'pending' && m.externalMatch !== null;

function SondaTorneo({
  torneo,
  soloIscrizione,
  profileType,
  riporta,
}: {
  torneo: LimuloTournament;
  /** Torneo non iniziato: solo il check-in al torneo, gli incontri non ci sono ancora. */
  soloIscrizione: boolean;
  profileType: string | null;
  riporta: (id: string, azioni: readonly LimuloAzionePendente[]) => void;
}) {
  const id = idDi(torneo);
  const [urgente, setUrgente] = useState(false);
  const base = urgente ? LIMULO_POLL_TORNEO_MS : LIMULO_POLL_LISTA_MS;

  const pIscrizione = useLimuloPolling();
  const iscrizione = useGetLimuloRegistrationQuery(id, pIscrizione.ritmo);
  useLimuloPollingGuida(pIscrizione, iscrizione, base);

  const pIncontri = useLimuloPolling();
  // Senza `participantId`: la lista del bordo e' gia' «i miei» (scope `mine`, `incontri.js:580`).
  const incontri = useGetLimuloMatchesQuery({ id }, { skip: soloIscrizione, ...pIncontri.ritmo });
  useLimuloPollingGuida(pIncontri, incontri, base);

  const [dettagli, setDettagli] = useState<Record<string, LimuloMatch | undefined>>({});
  // Le partite QGammon che questa sessione ha visto finire: il loro tavolo non e' piu' «da giocare»,
  // anche se il server chiude l'incontro solo dopo (v. `tavoliFiniti` in `azioniDelTorneo`).
  const finite = useAppSelector((s) => s.activeMatch.finite);
  const tavoliFiniti = useMemo(() => new Set(finite), [finite]);
  const suDettaglio = useCallback((matchId: string, m: LimuloMatch | undefined) => {
    setDettagli((prev) => (prev[matchId] === m ? prev : { ...prev, [matchId]: m }));
  }, []);

  const azioni = useMemo(
    () =>
      azioniDelTorneo({
        torneo,
        iscrizione: iscrizione.data,
        incontri: incontri.data,
        dettagli,
        profileType,
        servizioTavoli: servizioTavoliDellaBuild(),
        scacchi: chessLimuloAcceso(),
        tavoliFiniti,
        tennisClash: tennisClashDellaBuild(),
      }),
    [torneo, iscrizione.data, incontri.data, dettagli, profileType, tavoliFiniti],
  );

  useEffect(() => {
    riporta(id, azioni);
  }, [id, azioni, riporta]);
  // 12 s solo con una finestra di check-in d'incontro APERTA per il server (revisione #385, punto 2).
  useEffect(() => {
    setUrgente(finestraCheckInAperta({ incontri: incontri.data, dettagli }));
  }, [incontri.data, dettagli]);
  // Un torneo che esce dai vivi porta via le sue azioni.
  useEffect(() => () => riporta(id, []), [id, riporta]);

  return (
    <>
      {(incontri.data ?? []).filter(serveIlDettaglio).map((m) => (
        <SondaIncontro key={m.matchId} id={id} matchId={m.matchId} base={base} suDettaglio={suDettaglio} />
      ))}
    </>
  );
}

function SondaIncontro({
  id,
  matchId,
  base,
  suDettaglio,
}: {
  id: string;
  matchId: string;
  base: number;
  suDettaglio: (matchId: string, m: LimuloMatch | undefined) => void;
}) {
  const polling = useLimuloPolling();
  const dettaglio = useGetLimuloMatchQuery({ id, matchId }, polling.ritmo);
  useLimuloPollingGuida(polling, dettaglio, base);
  const m = dettaglio.data ?? undefined;
  useEffect(() => {
    suDettaglio(matchId, m);
  }, [matchId, m, suDettaglio]);
  useEffect(() => () => suDettaglio(matchId, undefined), [matchId, suDettaglio]);
  return null;
}

const ICONA: Record<LimuloTipoAzione, ComponentType<SVGProps<SVGSVGElement>>> = {
  sfida: Swords,
  invito: Gamepad2,
  'checkin-incontro': Swords,
  conferma: FileCheck,
  'checkin-torneo': LogIn,
  referto: FileCheck,
  gioca: Gamepad2,
};

function BarraAzioni({
  azioni,
  nonControllati,
  almeno,
  insetBottom,
  Riga,
}: {
  azioni: readonly LimuloAzionePendente[];
  nonControllati: number;
  almeno: boolean;
  insetBottom: boolean;
  Riga: RigaAzioneComponent;
}) {
  const { t } = useTranslation('limulo');
  const location = useLocation();
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { descriptor } = useActiveMatchContext();
  const { cta } = useMainCta();
  const modo = useModoBarraAzione();
  // Il biglietto di coda: il contesto risponde anche senza provider (a flag spento: nessun biglietto).
  const code = useCodeTornei();
  // Il bottone tondo B (con una partita viva, o minimizzata) sta nel flusso SOPRA la barra in basso e la partita viva: con la v3 contribuisce a `--ingombro-basso` come la riga del torneo (registro della 9a), cosi' il bottone dei minigiochi, che legge solo la variabile, sale sopra B e non lo copre (misurato 22/10 a 360×640 e 390×844: 48×39,5 px di sovrapposizione, centro di B coperto). Spenta la v3 il hook non fa niente.
  const ingombroB = useIngombroBasso();

  // Non si mostra l'azione che porta DOVE SEI: li' c'e' il bottone unico (o la scheda del torneo).
  // Una riga SENZA destinazione (la sfida di piattaforma): `destinazioneDi` e' `null`, che non e' mai il
  // pathname ⇒ resta su ogni pagina. Il filtro e' quello di sempre: la regola sta nel tipo di `destinazioneDi`.
  const visibili = useMemo(
    () =>
      azioni.filter(
        (a) =>
          // PONTE PROVVISORIO I-1317 (A7: la riga del ponte porta alla sua conversazione) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
          ((import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? destinazionePonteI1317(a) : null) ?? destinazioneDi(a)) !==
          location.pathname,
      ),
    [azioni, location.pathname],
  );

  const [minimizzata, setMinimizzata] = useState(false);
  const [aperta, setAperta] = useState(false);
  const viste = useRef<Set<string>>(new Set());

  // ⚠️ Azzeramento e riapertura leggono la lista INTERA, non `visibili` (revisione #385, punto 4):
  // `visibili` toglie l'azione della pagina in cui sei, e aprendo la destinazione dell'unica azione
  // la barra si sarebbe «svuotata» e poi riaperta tornando indietro, senza nessuna azione nuova.
  useEffect(() => {
    if (azioni.length === 0) {
      // Finite le azioni, si riparte da capo: la prossima si mostra intera.
      setMinimizzata(false);
      setAperta(false);
      return;
    }
    if (!minimizzata) return;
    // (b) — l'ordine conta: prima si guarda se c'e' un'azione NUOVA che riapre, POI si segnano come
    // viste quelle di adesso. Al contrario, la nuova risulterebbe «gia' vista» e non riaprirebbe mai.
    if (riapreLaBarra(viste.current, azioni)) {
      setMinimizzata(false);
      return;
    }
    for (const a of azioni) viste.current.add(a.chiave);
  }, [azioni, minimizzata]);

  const { plancia, orizzontale } = usePlanciaOrizzontale(descriptor != null);
  // SUL TAVOLO DI UNA PARTITA VIVA: il backgammon lo sa dal `descriptor`, gli scacchi e il bridge dalla rotta (`eTavoloVivoPerRotta`: il loro
  // socket sta nella rotta, non in `ActiveMatchContext`). Stesso ramo per tutti, niente comportamento nuovo (I-556; K9b-1, condizione 8).
  const partitaViva = descriptor != null || eTavoloVivoPerRotta(location.pathname);
  // La coda (B1 b, Luca 2026-09-25): c'e' un biglietto, a flag acceso. Il resto della regola (azioni,
  // partita viva, «Ranked» della Hub) sta in `rigaCodaOf`, che `BarraCoda` interroga.
  const inCoda = env.featureTournamentQueuesEnabled && code.ticket !== null;
  if (eSchedaIncontro(location.pathname)) return null;
  // L-2234 (a): nella conversazione DM, mentre la casella ha il fuoco, la barra non c'e'.
  if (modo === 'nascosta') return null;
  // N1 (revisione #385): senza azioni NIENTE barra — la nota sui tornei non controllati compare solo
  // accanto ad almeno un'azione (L-2195: «se rimangono azioni da fare»). ⚠️ Unica eccezione, decisa da
  // Luca (B1 b): la riga della coda, che non e' un'azione ma lo stato del giocatore.
  if (!partitaViva && visibili.length === 0) {
    return inCoda ? <BarraCoda inset={insetBottom && cta === null ? INSET_IN_BASSO : ''} /> : null;
  }
  // 🔴 [bloccante nuovo, revisione 19:23Z terza passata; scelta (a) di tma-orcr 28/9 20:00:57Z] La sfida
  // NON e' un'azione di torneo, e «un'azione VERA vince» sulla coda vale per quelle: con SOLO sfide la
  // riga della coda resta com'e' oggi e la sfida compare DOPO, come riga in piu' (sotto: la coda e' lo
  // stato del giocatore, la sfida di piattaforma vale 24 ore). Prima la sfida contava in `visibili.length` e
  // toglieva la coda: chi era in coda perdeva «Esci», e con `matched` anche «Partita trovata…».
  // ⚠️ La coda e' sopra e NON porta l'inset di sicurezza: l'ultima cosa sullo schermo e' la barra.
  const codaConSfide = !partitaViva && inCoda && visibili.every((a) => a.tipo === 'sfida');
  const conCodaSopra = (barra: ReactElement) =>
    codaConSfide ? (
      <>
        <BarraCoda inset="" />
        {barra}
      </>
    ) : (
      barra
    );

  const vai = (a: LimuloAzionePendente) => {
    haptics.impact('light');
    // Una riga senza destinazione non porta in nessun posto (e il suo corpo non chiama `vai`): non si naviga.
    // PONTE PROVVISORIO I-1317 (A7: la riga del ponte porta alla sua conversazione) — si toglie quando: P-a (qTournaments#225 e qlash-limulo#287 MERGED, `GET /v1/challenges/<id>` su staging porta `allenamento`, `src/lib/limulo/challenge.ts` lo manda).
    const dove = (import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? destinazionePonteI1317(a) : null) ?? destinazioneDi(a);
    if (dove === null) return;
    setAperta(false);
    navigate(dove);
  };

  /**
   * Le DUE azioni della sfida (I-252/I-254, T2-5), al posto del solo «Vai» (torna vuoto se la riga
   * non ha `pulsantiSfida`). La sfida sta IN CODA: puo' essere `prima` solo se non c'e' nessun'altra
   * azione; altrimenti compare fra le `altre`, con gli stessi due bottoni.
   * `stopPropagation` perche' i due bottoni sono SIBLING del bottone che apre la conversazione
   * (`RigaAzione`), non annidati: un tocco su Accetta/Rifiuta non deve anche navigare.
   * Due FORME (L-2595 (c), Luca 30/09 19:08Z): `larghi`, 44 px con la parola, sotto il titolo della scheda;
   * `tondi`, le icone di sempre accanto al Corpo nella riga bassa (le «altre» aperte, il modo riga del DM).
   * 🔴 In volo restano ENTRAMBI `disabled` (`accettando || rifiutando`, come prima: lo pretendono i banchi di
   * sfide-orcr). Cambia solo il COLORE, deciso da `aspettoSfida`: al posto del 50% su tutti e due, gira quello
   * che il flag nomina e l'altro diventa neutro.
   */
  const pulsantiSfida = (a: LimuloAzionePendente, forma: 'larghi' | 'tondi') => {
    const p = a.pulsantiSfida;
    if (!p) return null;
    const spento = p.accettando || p.rifiutando;
    const aspetto = aspettoSfida(p);
    const larghi = forma === 'larghi';
    const icona = (lato: AspettoBottoneSfida, Icona: typeof Check) =>
      lato === 'gira' ? (
        <Loader2 aria-hidden className={cn('animate-spin', larghi ? 'h-4.5 w-4.5' : 'h-5 w-5')} strokeWidth={2.4} />
      ) : (
        <Icona aria-hidden className={larghi ? 'h-4.5 w-4.5' : 'h-5 w-5'} strokeWidth={2.4} />
      );
    const forme = larghi
      ? 'flex h-11 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-extrabold'
      : 'grid h-9 w-9 shrink-0 place-items-center rounded-xl';
    const colore = (lato: AspettoBottoneSfida, acceso: string) => (lato === 'neutro' ? 'bg-bg-tertiary text-fg-muted' : acceso);
    return (
      <div className={larghi ? 'mt-2.5 flex gap-2' : 'flex shrink-0 gap-1.5'}>
        <button
          type="button"
          data-testid="azione-persistente-sfida-accetta"
          aria-label={t('persistente.sfida.accetta')}
          aria-busy={aspetto.accetta === 'gira' || undefined}
          disabled={spento}
          onClick={(e) => {
            e.stopPropagation();
            haptics.impact('light');
            p.onAccetta();
          }}
          className={cn(forme, 'transition-opacity active:opacity-80', colore(aspetto.accetta, larghi ? 'bg-success text-bg' : 'bg-success text-brand-fg'))}
        >
          {icona(aspetto.accetta, Check)}
          {larghi ? t('persistente.sfida.accettaBreve') : null}
        </button>
        <button
          type="button"
          data-testid="azione-persistente-sfida-rifiuta"
          aria-label={t('persistente.sfida.rifiuta')}
          aria-busy={aspetto.rifiuta === 'gira' || undefined}
          disabled={spento}
          onClick={(e) => {
            e.stopPropagation();
            haptics.impact('light');
            p.onRifiuta();
          }}
          className={cn(
            forme,
            'transition-opacity active:opacity-80',
            colore(aspetto.rifiuta, larghi ? 'text-fg ring-1 ring-inset ring-fg-muted/45' : 'bg-danger/80 text-brand-fg'),
          )}
        >
          {icona(aspetto.rifiuta, X)}
          {larghi ? t('persistente.sfida.rifiutaBreve') : null}
        </button>
      </div>
    );
  };

  // L'ultima cosa sullo schermo si prende l'inset di sicurezza: senza la barra in basso e senza
  // `MainCta` sotto, tocca a noi (stessa regola di `MainCta insetBottom`).
  const inset = insetBottom && cta === null ? INSET_IN_BASSO : '';

  /** B — NEL FLUSSO, allineato a destra: un cerchio che non copre niente (revisione #385, punto 5). */
  const bottoneB = (quante: number, onTocco: () => void, conPartitaViva: boolean) => (
    <div
      // Sopra la plancia orizzontale B e' `fixed` in alto: non sta in basso, e non contribuisce.
      ref={conPartitaViva && plancia ? undefined : ingombroB}
      data-plancia-orizzontale={conPartitaViva && plancia ? 'true' : undefined}
      className={cn(conPartitaViva && plancia ? 'fixed z-[60]' : 'flex justify-end px-4 pb-1', !plancia && inset)}
      style={conPartitaViva && plancia ? posizioneSopraLaPlancia(orizzontale) : undefined}
    >
      <button
        type="button"
        data-testid="azione-persistente-fab"
        data-partita-viva={conPartitaViva ? 'true' : undefined}
        aria-label={t('persistente.fab', { n: quante })}
        onClick={() => {
          haptics.selection();
          onTocco();
        }}
        className="relative grid h-12 w-12 place-items-center rounded-full bg-brand text-brand-fg shadow-[0_8px_24px_rgba(0,0,0,0.55)] transition-opacity active:opacity-80"
      >
        <Zap aria-hidden className="h-6 w-6" strokeWidth={2.4} />
        <span className="absolute -right-1 -top-1 min-w-6 rounded-full border-2 border-bg bg-warning px-1 text-center text-xs font-extrabold leading-5 text-bg">
          {quante}
        </span>
      </button>
    </div>
  );

  // 🔴 CON UNA PARTITA REAL-TIME VIVA — Luca, ~14:10Z (I-174, su L-2209), testuale: «1 b» = «solo il
  // bottone flottante col numero, e soltanto per check-in d'incontro o invito». Niente barra A; il
  // tocco porta DRITTO all'azione piu' urgente (la partita resta collegata: il socket vive in
  // `ActiveMatchProvider`). Per le altre azioni, niente. ⚠️ B sta nel flusso, quindi non copre la
  // plancia per costruzione: sulla schermata di gioco la accorcia di una riga, e solo finche' c'e'
  // un check-in o un invito. Non misurato a schermo sulle due schermate di gioco.
  if (partitaViva) {
    const urgenti = urgentiDelBottone(visibili);
    return urgenti.length === 0 ? null : bottoneB(urgenti.length, () => vai(urgenti[0]), true);
  }

  if (minimizzata && visibili.length > 0) {
    // B — la prima stesura lo faceva galleggiare sopra il contenuto: sulla scheda torneo copriva la
    // barra fissa dell'iscrizione, e ignorava l'inset di sicurezza (revisione #385, punto 5). Nel
    // flusso costa una riga d'altezza: e' il prezzo di non coprire un tasto altrui.
    return conCodaSopra(bottoneB(visibili.length, () => setMinimizzata(false), false));
  }

  const [prima, ...altre] = visibili;
  const nota =
    nonControllati > 0 ? (
      <button
        type="button"
        data-testid="azione-persistente-non-controllati"
        onClick={() => {
          haptics.impact('light');
          navigate(LIMULO_MINE_URL);
        }}
        className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-xs font-semibold text-fg-muted"
      >
        <ListChecks aria-hidden className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          {t(almeno ? 'persistente.nonControllatiAlmeno' : 'persistente.nonControllati', { n: nonControllati })}
        </span>
        <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0" />
      </button>
    ) : null;
  // `prima` c'e' sempre qui: senza azioni visibili si e' gia' usciti (N1).
  if (prima === undefined) return null;
  // L-2234 (a): nella conversazione DM la barra resta, ridotta a UNA riga: solo l'azione piu' urgente,
  // senza «+N», senza freccia per ridurla e senza la nota (la casella di scrittura e' la cosa principale).
  // L-2595 (c): qui la scheda non c'e' mai, anche per un'azione calda — derivazione del cantiere (una scheda
  // da 130 px sopra la casella del DM ruberebbe proprio lo spazio che L-2234 aveva liberato).
  if (modo === 'riga') {
    return conCodaSopra(
      <div className={inset}>
        <div
          data-testid="azione-persistente"
          data-modo="riga"
          className="mx-3 mb-1 overflow-hidden rounded-2xl border border-brand/40 bg-bg-secondary"
        >
          <Riga azione={prima} onVai={() => vai(prima)} peso="bassa" riga sfida={pulsantiSfida(prima, 'tondi')} />
        </div>
      </div>,
    );
  }
  // L-2595 (c) «due pesi»: la PRIMA riga prende la scheda se e' un'azione che si perde a tavolino
  // (`eCalda`); le altre, anche calde, restano basse. Chi decide QUALE e' la prima resta la logica.
  const pesoPrima: PesoRiga = eCalda(prima.tipo) ? 'scheda' : 'bassa';
  const inTesta = (
    <>
      {altre.length > 0 ? (
        <button
          type="button"
          data-testid="azione-persistente-altre"
          aria-label={t('persistente.altreAria', { n: altre.length })}
          aria-expanded={aperta}
          onClick={() => {
            haptics.selection();
            setAperta((v) => !v);
          }}
          className="shrink-0 rounded-full bg-bg-tertiary px-2 py-1 text-xs font-extrabold text-fg"
        >
          {t('persistente.altre', { n: altre.length })}
        </button>
      ) : null}
      <button
        type="button"
        data-testid="azione-persistente-riduci"
        aria-label={t('persistente.riduci')}
        onClick={() => {
          haptics.selection();
          viste.current = new Set(azioni.map((a) => a.chiave));
          setAperta(false);
          setMinimizzata(true);
        }}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-fg-muted transition-opacity active:opacity-70"
      >
        <ChevronDown aria-hidden className="h-5 w-5" />
      </button>
    </>
  );
  return conCodaSopra(
    <div className={inset}>
      <div
        data-testid="azione-persistente"
        data-peso={pesoPrima}
        className={cn(
          'mx-3 mb-1 overflow-hidden rounded-2xl border bg-bg-secondary shadow-[0_10px_30px_rgba(0,0,0,0.45)]',
          pesoPrima === 'scheda' ? 'border-warning/60' : 'border-brand/40',
        )}
      >
        {aperta
          ? altre.map((a) => (
              <div key={a.chiave} className="border-b border-border">
                <Riga azione={a} onVai={() => vai(a)} peso="bassa" sfida={pulsantiSfida(a, 'tondi')} />
              </div>
            ))
          : null}
        {/* 🔴 [bloccante, revisione 28/9 18:50Z] «+N»/«riduci» ci sono sempre, anche con una sfida `prima`
            (la sfida sta IN CODA, `LIMULO_TIPI_AZIONE`). L-2595 (c): nella scheda stanno in alto a destra,
            FRATELLI del Corpo; nella riga bassa in fondo, come prima. */}
        <Riga
          azione={prima}
          onVai={() => vai(prima)}
          peso={pesoPrima}
          sfida={pulsantiSfida(prima, pesoPrima === 'scheda' ? 'larghi' : 'tondi')}
          inTesta={inTesta}
          quantiInTesta={altre.length > 0 ? 2 : 1}
        />
        {nota}
      </div>
    </div>,
  );
}

const INSET_IN_BASSO = 'pb-[max(env(safe-area-inset-bottom,0px),var(--tg-viewport-safe-area-inset-bottom,0px))]';

/**
 * La barra quando c'e' solo la coda. ⚠️ Un componente a se', montato SOLO con un biglietto in mano: porta
 * con se' store (sezione della Hub, dispatch) e router, e i banchi della barra che non parlano di code
 * non devono montarli (stessa lezione di `BottoneTavolo`, #392).
 */
function BarraCoda({ inset }: { inset: string }) {
  const code = useCodeTornei();
  const dispatch = useAppDispatch();
  const sezioneHub = useAppSelector((s) => s.core.hubSection);
  const location = useLocation();
  const navigate = useNavigate();
  const haptics = useHaptics();
  // r14: UNA riga per gioco in attesa (`ticketsToTell`). Prima la barra ne raccontava uno solo (`code.ticket`): con due
  // giochi in coda «Esci» usciva da quello e l'altro rinnovava senza riga. Con UN gioco solo e' quel biglietto, come prima.
  const biglietti = ticketsToTell(code.tickets, code.ticket);
  // Col pannello «Sit&Go» acceso (L-2285) il biglietto lo racconta anche lui: li' la riga non si ripete,
  // e «Vai» porta dove si aspetta. Il tipo delle code sta nella vetrina: la stessa voce di cache dei
  // pannelli, letta qui SOLO a sit&go acceso e senza ritmo suo (a flag spento, nessuna chiamata) — e
  // solo mentre si aspetta: a biglietto chiuso (`idle`) la riga c'e' solo nel raro idle/abbinato senza
  // torneo («Partita trovata…»), e li' «Vai» va comunque alla Ranked; con `refetchOnFocus` ogni ritorno in
  // primo piano sarebbe una GET della vetrina per niente (revisioni di #447 e #448).
  const sitngo = sitngoDiQtournamentsAcceso();
  const attesaAperta = biglietti.some((b) => b.state === 'waiting' || b.state === 'matched');
  const vetrina = useGetLimuloQueuesQuery(undefined, { skip: !sitngo || !attesaAperta });
  // «Vai» porta alla Hub CR: un biglietto di un gioco che li' non ha la sua scheda non ci va. Il catalogo e' la
  // voce di cache che l'app legge gia' all'avvio (`CodeTorneiContext`), qui solo mentre si aspetta.
  const giochi = useGetLimuloGamesQuery(undefined, { skip: !attesaAperta });
  // Il gioco attivo: i due giochi hanno schede con la stessa chiave (`'ranked'`), e la riga si nasconde solo dove il
  // pannello racconta QUESTO biglietto. `?.`: il banco della barra non porta `activeGames`.
  const giocoAttivoId = useAppSelector((s) => s.core.activeGames?.[0] ?? null);
  const backgammonAttivo = giocoAttivoId === BACKGAMMON_GAME_ID;
  const righe = biglietti.flatMap((ticket) => {
    const sezione = sezioneDellaCodaOf({
      ticket,
      queues: vetrina.data?.queues ?? null,
      sitngoAcceso: sitngo,
    });
    const vai = codaHaUnaScheda({
      ticket,
      games: giochi.data,
      conScheda: giochiConCodeDaRiprendere(),
    });
    // Di che gioco e' l'attesa: il backgammon ha il suo «Sit&Go» (a flag, `sitngoBackgammonAcceso`), e li' «Vai» cambia
    // il gioco attivo e apre quella scheda. Catalogo non letto o gioco non nominato ⇒ Clash Royale, com'e' sempre stato.
    const profileType = profileTypeDelBiglietto(ticket, giochi.data);
    const gioco: 'cr' | 'bg' =
      profileType === BACKGAMMON_PROFILE_TYPE &&
      giochiConCodeDaRiprendere().includes(BACKGAMMON_PROFILE_TYPE)
        ? 'bg'
        : 'cr';
    const riga = rigaCodaOf({
      ticket,
      azioni: 0,
      partitaViva: false,
      suPannelloCode: suPannelloDellaCodaOf({
        gioco,
        pathname: location.pathname,
        sezioneHub,
        backgammonAttivo,
        sitngoCrAcceso: sitngo,
      }),
    });
    if (riga === null) return [];
    const nome = profileType === null ? null : (CODE_GAME_NAMES[profileType] ?? null);
    return [{ gameId: ticket.gameId, nome, gioco, sezione, vai, riga }];
  });
  if (righe.length === 0) return null;
  return (
    <div className={inset}>
      {righe.map((r) => (
        <RigaCoda
          key={r.gameId}
          gameId={r.gameId}
          // Il nome del gioco solo quando le righe sono piu' d'una: con UNA il testo e' quello di sempre.
          gioco={righe.length > 1 ? r.nome : null}
          riga={r.riga}
          busy={code.busy}
          vai={r.vai}
          onVai={() => {
            haptics.impact('light');
            // La stessa apertura di «Torna alla coda» della modale d'esito (`lib/limulo/tornaAllaCoda.ts`): una definizione sola.
            apriSchedaDellaCoda(dispatch, { gioco: r.gioco, sezione: r.sezione }, giocoAttivoId);
            navigate('/');
          }}
          // «Esci» esce da QUEL gioco e da nessun altro: `leave(undefined, gameId)`, non il biglietto «toccato per ultimo».
          onEsci={() => {
            haptics.impact('medium');
            void code.leave(undefined, r.gameId);
          }}
        />
      ))}
    </div>
  );
}

/**
 * La riga della coda (B1 b). Il tempo corre ogni secondo SOLO a barra visibile: il componente esiste
 * solo quando la barra lo mostra, e ad app nascosta il giro salta (nessun ridisegno che nessuno vede).
 * ⛔ Nessuna chiamata nuova: il biglietto e' quello del contesto (`CodeTorneiContext`, #390), «Esci» e'
 * il suo `leave` (con la gestione di `queue.not-waiting`), il tocco porta al pannello della Hub CR dove si
 * aspetta (`sezioneDellaCodaOf`: la «Ranked», o il «Sit&Go» a flag se ogni attesa e' un sit&go).
 */
function RigaCoda({
  gameId,
  gioco,
  riga,
  busy,
  vai,
  onVai,
  onEsci,
}: {
  /** Il gioco del biglietto: una riga per gioco (r14). */
  gameId: string;
  /** Il nome del gioco, solo con piu' righe insieme; `null` = il testo di sempre. */
  gioco: string | null;
  riga: LimuloRigaCoda;
  busy: boolean;
  /** «Vai» ha una scheda dove portare (`codaHaUnaScheda`); falso = la riga resta, il tocco no. */
  vai: boolean;
  onVai: () => void;
  onEsci: () => void;
}) {
  const { t } = useTranslation('limulo');
  const [adesso, setAdesso] = useState(() => Date.now());
  const da = riga.kind === 'in-coda' ? riga.da : null;
  useEffect(() => {
    if (da === null) return;
    const h = window.setInterval(() => {
      if (document.visibilityState === 'visible') setAdesso(Date.now());
    }, 1000);
    return () => window.clearInterval(h);
  }, [da]);
  const testo =
    riga.kind === 'abbinato'
      ? t('persistente.coda.abbinato')
      : da !== null
        ? t('persistente.coda.inCodaDa', { tempo: formatElapsed(adesso - da) })
        : t('persistente.coda.inCoda');
  return (
    <div
      data-testid="azione-persistente-coda"
      data-coda={riga.kind}
      data-game-id={gameId}
      className="mx-3 mb-1 flex items-center gap-2 overflow-hidden rounded-2xl border border-brand/50 bg-bg-secondary px-3 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
    >
      <button
        type="button"
        data-testid="azione-persistente-coda-vai"
        aria-label={t('persistente.coda.aria')}
        disabled={!vai}
        onClick={onVai}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        {riga.kind === 'abbinato' ? (
          <Swords aria-hidden className="h-5 w-5 shrink-0 text-success" />
        ) : (
          <Loader2 aria-hidden className="h-5 w-5 shrink-0 animate-spin text-brand" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-fg">
          {gioco === null ? testo : t('persistente.coda.conGioco', { gioco, testo })}
        </span>
      </button>
      {riga.kind === 'in-coda' ? (
        // L-2595: acceso a CONTORNO — `bg-bg-tertiary` + `text-fg-muted` e' l'aspetto del bottone SPENTO (#502),
        // e «Esci» acceso lo portava gia'. In volo: grigio pieno con la rotella, invece del 50%.
        <button
          type="button"
          data-testid="azione-persistente-coda-esci"
          disabled={busy}
          aria-busy={busy || undefined}
          onClick={onEsci}
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold active:opacity-80',
            busy ? 'border-transparent bg-bg-tertiary text-fg-muted' : 'border-fg-muted/45 text-fg',
          )}
        >
          {busy ? <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" /> : null}
          {t('persistente.coda.esci')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Il PESO di una riga (L-2595 (c), Luca 2026-09-30T19:08Z, testuale «(c) due pesi»): la `scheda` — occhiello,
 * titolo e un bottone largo che dice dove porta — per la prima riga quando e' un'azione che si perde a
 * tavolino; la riga `bassa` (due righe piccole) per tutte le altre. Proposta e tavole:
 * `qlash-dev-knowledge/services/qlash-telegram-app/ui/2026-09-30-barra-incontro-proposte.md`.
 * ⚠️ Derivazione del cantiere, dichiarata nel consiglio: nella tavola (c) la riga calma era UNA riga e perdeva
 * il round («Coppa d'Autun…»); qui e' su due.
 */
export type PesoRiga = 'scheda' | 'bassa';

/**
 * I tipi che si perdono a tavolino se non li vedi: nella barra sono ambra, e in testa prendono la scheda.
 * Era il `caldo` di `RigaAzione`, stesso insieme. ⚠️ ASPETTO, non logica: cosa compare e in che ordine lo
 * decide `lib/limulo/azionePersistente.ts` (di tma-orcr).
 */
const TIPI_CALDI: ReadonlySet<LimuloTipoAzione> = new Set(['invito', 'checkin-incontro', 'conferma', 'sfida']);
const eCalda = (tipo: LimuloTipoAzione): boolean => TIPI_CALDI.has(tipo);

/**
 * Le parole del bottone largo della scheda. Solo i tipi caldi con una destinazione: la sfida ha Accetta/Rifiuta.
 * Un tipo caldo nuovo senza chiave ripiega su «Vai» (`persistente.vai`), mai su una chiave assente.
 */
const CHIAVE_BOTTONE_LARGO: ReadonlyMap<LimuloTipoAzione, string> = new Map([
  ['checkin-incontro', 'persistente.cta.checkin-incontro'],
  ['conferma', 'persistente.cta.conferma'],
  ['invito', 'persistente.cta.invito'],
]);

type AspettoBottoneSfida = 'acceso' | 'gira' | 'neutro';

/**
 * Il COLORE dei due bottoni della sfida (L-2595). Solo aspetto: `disabled` resta `accettando || rifiutando`.
 * ⚠️ `accettando` non vuol dire solo «Accetta in volo»: lo accendono anche la lettura dei profili
 * (`inLettura`), un tocco in volo dall'ALTRA superficie (`daAltrove` in `useRispostaSfida`, qualunque sia il
 * lato toccato la') e un avviso gia' scritto (`SorgenteSfidePiattaforma`). Con un esito o un errore nella riga
 * non gira niente; per il resto la rotella va sul lato che il flag nomina — la barra non ne sa di piu'.
 */
function aspettoSfida(p: NonNullable<LimuloAzionePendente['pulsantiSfida']>): {
  accetta: AspettoBottoneSfida;
  rifiuta: AspettoBottoneSfida;
} {
  if (!p.accettando && !p.rifiutando) return { accetta: 'acceso', rifiuta: 'acceso' };
  if (p.esito || p.errore) return { accetta: 'neutro', rifiuta: 'neutro' };
  if (p.rifiutando) return { accetta: 'neutro', rifiuta: 'gira' };
  return { accetta: 'gira', rifiuta: 'neutro' };
}

/**
 * Le props di una riga della barra: la forma che ogni `Riga` passata ad `AzionePersistente` deve accettare (la v3 ne passa una sua, PR 9b: stessa logica, altro aspetto). Esportata
 * perche' chi disegna la riga nuova la tipa con questa, non con una copia.
 */
export type PropsRigaAzione = {
  azione: LimuloAzionePendente;
  onVai: () => void;
  peso: PesoRiga;
  /** Accetta/Rifiuta (`pulsantiSfida`): FRATELLI del Corpo, mai dentro — sotto il titolo nella scheda, accanto nella riga bassa. */
  sfida?: ReactNode;
  /** «+N» e «riduci»: in fondo alla riga bassa; nella scheda in alto a destra, sopra il margine che la riga dell'occhiello lascia loro. */
  inTesta?: ReactNode;
  /** Quanti bottoni porta `inTesta`: il margine destro della riga dell'occhiello nella scheda. */
  quantiInTesta?: 0 | 1 | 2;
  /** Una riga sola (L-2234 (a)): resta l'occhiello con la scadenza, cade il titolo. */
  riga?: boolean;
};
export type RigaAzioneComponent = ComponentType<PropsRigaAzione>;

export function RigaAzione({ azione, onVai, peso, sfida = null, inTesta = null, quantiInTesta = 0, riga = false }: PropsRigaAzione) {
  const { t } = useTranslation('limulo');
  // Solo TESTO: il conto alla rovescia non accende, non spegne e non riordina niente (ADR-0158).
  const manca = useCountdown(azione.scadenza);
  const Icona = ICONA[azione.tipo];
  const occhiello =
    azione.tipo === 'invito' && azione.tag
      ? t('persistente.tipo.invito', { tag: azione.tag })
      : t(`persistente.tipo.${azione.tipo}`);
  // Corpo INERTE per una riga senza destinazione: non e' un bottone, non naviga e non ha ruolo di link. Titolo e inerzia sono di `useTitoloAzione`, condiviso con la riga della v3.
  const { titolo, inerte } = useTitoloAzione(azione);
  const caldo = eCalda(azione.tipo);
  const scheda = peso === 'scheda';
  const Corpo = inerte ? 'div' : 'button';

  const tessera = (
    <span
      className={cn(
        'grid h-7 w-7 shrink-0 place-items-center rounded-full',
        scheda ? 'bg-warning text-bg' : caldo ? 'bg-warning/20 text-warning' : 'bg-brand/15 text-brand',
      )}
    >
      <Icona aria-hidden className="h-4 w-4" strokeWidth={2.4} />
    </span>
  );
  /* Il conto e' FRATELLO dell'occhiello, mai figlio dello span che tronca: su uno schermo stretto a sparire deve
     essere l'occhiello (con i puntini), non la scadenza. L-2595: e' un BOLLINO col timer, ma il testo resta
     « · M:SS» (il « · » va ai lettori di schermo) — contratto con sfide-orcr: dentro `azione-persistente-vai`,
     `tabular-nums` solo qui, `data-testid="azione-persistente-conto"`. `whitespace-pre` tiene lo spazio in testa. */
  const conto =
    manca !== null ? (
      <span
        data-testid="azione-persistente-conto"
        className={cn(
          'inline-flex shrink-0 items-center whitespace-pre rounded-full bg-warning/15 tabular-nums text-warning',
          scheda ? 'ml-auto gap-1 px-2 py-0.5 text-[13px] leading-[18px] tracking-normal normal-case' : 'ml-1.5 gap-0.5 px-1.5',
        )}
      >
        <span className="sr-only">{' · '}</span>
        <Timer aria-hidden className={scheda ? 'h-3.5 w-3.5' : 'h-3 w-3'} strokeWidth={2.4} />
        {manca}
      </span>
    ) : null;
  const colonna = (
    <span className="min-w-0 flex-1">
      <span
        className={cn(
          'flex items-center font-extrabold uppercase tracking-wider',
          caldo ? 'text-warning' : 'text-brand',
          scheda ? 'min-h-7 text-[11px]' : 'text-[10.5px] leading-[14px]',
          scheda && (quantiInTesta === 2 ? 'pr-20' : quantiInTesta === 1 ? 'pr-10' : ''),
        )}
      >
        <span data-testid="azione-persistente-occhiello" className="min-w-0 truncate">
          {occhiello}
        </span>
        {conto}
      </span>
      {riga ? null : (
        <span className={cn('block truncate font-bold text-fg', scheda ? 'text-base leading-[22px]' : 'text-[13px] leading-[18px]')}>
          {titolo}
        </span>
      )}
    </span>
  );
  const corpo = (
    <Corpo
      {...(inerte ? {} : { type: 'button' as const, onClick: onVai })}
      data-testid="azione-persistente-vai"
      data-tipo={azione.tipo}
      className={cn(
        scheda ? 'block w-full text-left' : 'flex min-w-0 flex-1 items-center gap-2 text-left',
        !inerte && 'transition-opacity active:opacity-80',
      )}
    >
      {scheda ? (
        <span className="flex items-start gap-2">
          {tessera}
          {colonna}
        </span>
      ) : (
        <>
          {tessera}
          {colonna}
        </>
      )}
      {/* Il ramo `sfida` non ha il bottone: Accetta/Rifiuta vivono in `sfida`, FRATELLI di questo Corpo — MAI
          annidati. Il bottone largo e' uno SPAN e la freccia un'icona (`<svg>`), entrambi dentro il Corpo (che e' gia' il `<button>`): un solo
          `azione-persistente-vai` per riga, e la regola del bottone spento (`button.bg-brand:disabled`) non li
          riguarda — il Corpo non si spegne mai. */}
      {azione.pulsantiSfida ? null : scheda ? (
        <span className="mt-2.5 flex h-11 w-full items-center justify-center gap-1 rounded-lg bg-brand text-sm font-extrabold text-brand-fg">
          {t(CHIAVE_BOTTONE_LARGO.get(azione.tipo) ?? 'persistente.vai')}
          <ChevronRight aria-hidden className="h-4 w-4" strokeWidth={2.4} />
        </span>
      ) : (
        <ChevronRight aria-hidden className="h-4.5 w-4.5 shrink-0 text-brand" strokeWidth={2.4} />
      )}
    </Corpo>
  );

  return (
    <>
      {scheda ? (
        <div data-peso="scheda" className="relative px-3 pb-3 pt-2.5">
          {corpo}
          {inTesta ? <div className="absolute right-2 top-2 flex items-center gap-1.5">{inTesta}</div> : null}
          {sfida}
        </div>
      ) : (
        <div data-peso="bassa" className={cn('flex items-center gap-2', riga ? 'px-3 py-2' : 'py-1.5 pl-3 pr-2')}>
          {corpo}
          {sfida}
          {inTesta}
        </div>
      )}
      {/* 🔴 [bloccante 3, revisione 19:23Z] L'errore di Accetta/Rifiuta dalla barra, VISIBILE nella riga:
          la live region condivisa (`annuncia`) e' nascosta e non serve a chi vede. Fragment, non un wrapper:
          i due slot restano due e distinti (errore `role="alert"`, esito `role="status"`), in entrambi i pesi. */}
      {azione.pulsantiSfida?.esito ? (
        <p
          role="status"
          data-testid="azione-persistente-sfida-esito"
          className="px-3 pb-2 text-xs font-semibold text-success"
        >
          {azione.pulsantiSfida.esito}
        </p>
      ) : null}
      {azione.pulsantiSfida?.collegando ? (
        <p
          role="status"
          data-testid="azione-persistente-sfida-collegando"
          className="px-3 pb-2 text-xs text-fg-muted"
        >
          {azione.pulsantiSfida.collegando}
        </p>
      ) : null}
      {azione.pulsantiSfida?.errore ? (
        <p
          role="alert"
          data-testid="azione-persistente-sfida-errore"
          className="px-3 pb-2 text-xs font-semibold text-danger"
        >
          {azione.pulsantiSfida.errore}
        </p>
      ) : null}
    </>
  );
}

/**
 * N2 (revisione #385): il tabellone ORIZZONTALE del backgammon e' uno strato `fixed inset-0 z-50`
 * (`BackgammonMatch.tsx`, la regione `lscape-info`), e B nel flusso gli finirebbe sotto — invisibile
 * proprio nel caso di I-174. Si riconosce dal suo `data-section` (lo stesso che legge il banco a
 * pixel), e solo con una partita viva.
 */
function usePlanciaOrizzontale(attivo: boolean): { plancia: boolean; orizzontale: boolean } {
  const [stato, setStato] = useState({ plancia: false, orizzontale: false });
  useEffect(() => {
    if (!attivo) {
      setStato({ plancia: false, orizzontale: false });
      return;
    }
    const leggi = () => {
      const plancia = document.querySelector('[data-section="lscape-info"]') !== null;
      const orizzontale = window.innerWidth > window.innerHeight;
      setStato((prev) => (prev.plancia === plancia && prev.orizzontale === orizzontale ? prev : { plancia, orizzontale }));
    };
    leggi();
    const spia = new MutationObserver(leggi);
    spia.observe(document.body, { childList: true, subtree: true });
    // 🔴 La ROTAZIONE del telefono non tocca il DOM se nient'altro cambia (revisione #385, quarta
    // passata): senza questo ascolto B restava sul lato di prima, cioe' col tabellone ruotato
    // sull'angolo basso della colonna, dove sta «Resign».
    window.addEventListener('resize', leggi);
    return () => {
      spia.disconnect();
      window.removeEventListener('resize', leggi);
    };
  }, [attivo]);
  return stato;
}

/**
 * Sopra la plancia orizzontale, nell'angolo ALTO della colonna delle INFORMAZIONI (a sinistra nel
 * contenuto: «match to», punteggio) — non dadi ne' comandi, che stanno al centro e a destra, e non il
 * «Resign», in fondo alla colonna. Col telefono in orizzontale e' l'angolo in alto a sinistra; col
 * telefono in verticale e il tabellone RUOTATO di 90° lo stesso angolo del contenuto cade in alto a
 * DESTRA dello schermo. Con gli inset di sicurezza.
 * ⚠️ NON MISURATO a schermo: la posizione e' dedotta dal codice del tabellone, non da uno scatto.
 * ⚠️ Dipende da `data-section="lscape-info"`: se sparisse, B tornerebbe nel flusso SOTTO lo strato del
 * tabellone, invisibile e senza avviso — `tools/verify-landscape-layout.ts` tiene il legame.
 */
function posizioneSopraLaPlancia(orizzontale: boolean): CSSProperties {
  const lato = orizzontale ? 'left' : 'right';
  return {
    top: 'calc(max(env(safe-area-inset-top, 0px), var(--tg-viewport-safe-area-inset-top, 0px)) + 0.5rem)',
    [lato]: `calc(max(env(safe-area-inset-${lato}, 0px), var(--tg-viewport-safe-area-inset-${lato}, 0px)) + 0.5rem)`,
  };
}
